import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { withLocalePrefix } from '../../../shared/utils/translation';
import { consumer } from './cable';
import { standardRequestWithErrorFlash } from '../../../shared/utils/request';

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

type AgentChatContextValue = {
  messages: ChatMessage[];
  streamingContent: string;
  isStreaming: boolean;
  error: string | null;
  sendMessage: (text: string) => Promise<void>;
};

const STREAM_TIMEOUT_MS = 30000;
const t = withLocalePrefix('candidate_profile_redesign.agent_chat');

const AgentChatContext = createContext<AgentChatContextValue | null>(null);

type AgentChatProviderProps = {
  applicationId: number;
  children: ReactNode;
};

export const AgentChatProvider = ({
  applicationId,
  children,
}: AgentChatProviderProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chatIdRef = useRef<number | null>(null);
  const subscriptionRef = useRef<ReturnType<
    typeof consumer.subscriptions.create
  > | null>(null);
  const streamTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const receivedChunkRef = useRef(false);

  const clearStreamTimeout = useCallback(() => {
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }
  }, []);

  const subscribe = useCallback(
    (chatId: number) => {
      subscriptionRef.current?.unsubscribe();

      subscriptionRef.current = consumer.subscriptions.create(
        {
          channel: 'Agents::Channels::ChatChannel',
          chat_id: chatId,
        },
        {
          received(data: {
            type: string;
            content?: string;
            message_id?: number;
            message?: string;
          }) {
            switch (data.type) {
              case 'chunk':
                receivedChunkRef.current = true;
                clearStreamTimeout();
                setStreamingContent((prev) => prev + (data.content ?? ''));
                break;
              case 'message_break':
                setStreamingContent((prev) => {
                  if (prev) {
                    setMessages((msgs) => [
                      ...msgs,
                      {
                        id: `assistant-${Date.now()}-${msgs.length}`,
                        role: 'assistant',
                        content: prev,
                      },
                    ]);
                  }
                  return '';
                });
                break;
              case 'done':
                clearStreamTimeout();
                setStreamingContent((prev) => {
                  if (prev) {
                    setMessages((msgs) => [
                      ...msgs,
                      {
                        id: `assistant-${data.message_id ?? Date.now()}`,
                        role: 'assistant',
                        content: prev,
                      },
                    ]);
                  }
                  return '';
                });
                setIsStreaming(false);
                break;
              case 'error':
                clearStreamTimeout();
                setError(data.message ?? t('error_default'));
                setStreamingContent('');
                setIsStreaming(false);
                break;
            }
          },
        }
      );
    },
    [clearStreamTimeout]
  );

  // Teardown only runs when the profile unmounts (page navigation/reload).
  useEffect(() => {
    return () => {
      subscriptionRef.current?.unsubscribe();
      clearStreamTimeout();
    };
  }, [clearStreamTimeout]);

  const sendMessage = useCallback(
    async (text: string) => {
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: text,
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);
      setError(null);
      setStreamingContent('');
      receivedChunkRef.current = false;

      try {
        const response = await standardRequestWithErrorFlash.post<{
          chat_id: number;
        }>('/candidate_agent/chats', {
          message: text,
          application_id: applicationId,
          chat_id: chatIdRef.current,
        });

        const newChatId = response.data.chat_id;

        if (chatIdRef.current !== newChatId || !subscriptionRef.current) {
          chatIdRef.current = newChatId;
          subscribe(newChatId);
        }

        streamTimeoutRef.current = setTimeout(() => {
          if (!receivedChunkRef.current) {
            setError(t('error_timeout'));
            setStreamingContent('');
            setIsStreaming(false);
          }
        }, STREAM_TIMEOUT_MS);
      } catch {
        setError(t('error_send_failed'));
        setIsStreaming(false);
      }
    },
    [applicationId, subscribe]
  );

  return (
    <AgentChatContext.Provider
      value={{
        messages,
        streamingContent,
        isStreaming,
        error,
        sendMessage,
      }}
    >
      {children}
    </AgentChatContext.Provider>
  );
};

export const useAgentChat = (): AgentChatContextValue => {
  const ctx = useContext(AgentChatContext);
  if (!ctx) {
    throw new Error('useAgentChat must be used within AgentChatProvider');
  }
  return ctx;
};
